import { useEffect, useState, useCallback } from "react";
import { Boxes, Plus, X, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiErrorMessage } from "@/lib/api";
import { DataTable } from "@/components/common/DataTable";
import { PageHeader, TenantPicker, useTenantScope, Badge } from "./shared";

const CLASSES = ["Public", "Internal", "PII", "PHI", "Sensitive"];
const BASES = ["consent", "contract", "legal_obligation", "vital_interest", "public_task", "legitimate_interest"];

export default function DataInventoryPage() {
  const scope = useTenantScope();
  const [data, setData] = useState({ items: [], total: 0, phiCount: 0 });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ dataCategory: "", description: "", collections: "", purpose: "", legalBasis: "contract", retentionDays: 365, classification: "PII" });

  const load = useCallback(async () => {
    setLoading(true);
    try { setData((await api.get("/v1/compliance/data-inventory", { params: scope.params })).data.data); }
    catch { setData({ items: [], total: 0, phiCount: 0 }); }
    finally { setLoading(false); }
  }, [scope.params]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.dataCategory.trim()) { toast.error("Data category required"); return; }
    const payload = {
      ...form,
      collections: form.collections.split(",").map((s) => s.trim()).filter(Boolean),
      retentionDays: Number(form.retentionDays) || null,
      isPHI: form.classification === "PHI",
      isPII: ["PII", "PHI", "Sensitive"].includes(form.classification),
      ...scope.params,
    };
    try {
      await api.post("/v1/compliance/data-inventory", payload);
      toast.success("Inventory entry created");
      setShowForm(false);
      setForm({ dataCategory: "", description: "", collections: "", purpose: "", legalBasis: "contract", retentionDays: 365, classification: "PII" });
      load();
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/v1/compliance/data-inventory/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="data-inventory-page">
      <PageHeader icon={Boxes} title="Data Inventory (RoPA)"
        subtitle={`Record of Processing Activities · ${data.total} categories · ${data.phiCount} PHI`}>
        <TenantPicker {...scope} />
        <button onClick={() => setShowForm(true)} className="inline-flex items-center gap-2 rounded-lg bg-teal-600 hover:bg-teal-700 text-white text-sm px-3 py-1.5" data-testid="new-inventory-btn">
          <Plus className="h-4 w-4" /> New entry
        </button>
      </PageHeader>

      <DataTable testid="inventory-table" rows={data.items} loading={loading}
        columns={[
          { key: "dataCategory", header: "Category", render: (i) => <div><div className="font-medium text-foreground">{i.dataCategory}</div><div className="text-xs text-muted-foreground">{i.description}</div></div> },
          { key: "classification", header: "Class", render: (i) => <Badge label={i.classification} kind="classification" testid={`class-${i.id}`} /> },
          { key: "purpose", header: "Purpose", render: (i) => <span className="text-xs">{i.purpose}</span> },
          { key: "legalBasis", header: "Legal basis", render: (i) => <span className="text-xs capitalize">{(i.legalBasis || "").replace("_", " ")}</span> },
          { key: "retentionDays", header: "Retention", render: (i) => i.retentionDays ? `${i.retentionDays}d` : "—" },
          { key: "collections", header: "Collections", render: (i) => <span className="font-mono text-[11px] text-muted-foreground">{(i.collections || []).join(", ")}</span> },
          { key: "actions", header: "", render: (i) => <button onClick={() => remove(i.id)} className="text-red-500 hover:text-red-600" data-testid={`del-inventory-${i.id}`}><Trash2 className="h-4 w-4" /></button> },
        ]} />

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="new-inventory-modal">
          <div className="bg-card rounded-xl border border-border w-full max-w-lg p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-foreground">New data inventory entry</h3>
              <button onClick={() => setShowForm(false)}><X className="h-5 w-5 text-muted-foreground" /></button>
            </div>
            <div className="space-y-3">
              <input placeholder="Data category" value={form.dataCategory}
                onChange={(e) => setForm({ ...form, dataCategory: e.target.value })}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="inv-category-input" />
              <input placeholder="Description" value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
              <input placeholder="Collections (comma separated)" value={form.collections}
                onChange={(e) => setForm({ ...form, collections: e.target.value })}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="inv-collections-input" />
              <input placeholder="Purpose" value={form.purpose}
                onChange={(e) => setForm({ ...form, purpose: e.target.value })}
                className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
              <div className="grid grid-cols-3 gap-2">
                <select value={form.classification} onChange={(e) => setForm({ ...form, classification: e.target.value })}
                  className="rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="inv-class-select">
                  {CLASSES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                <select value={form.legalBasis} onChange={(e) => setForm({ ...form, legalBasis: e.target.value })}
                  className="rounded-lg border border-border bg-background px-3 py-2 text-sm">
                  {BASES.map((b) => <option key={b} value={b}>{b.replace("_", " ")}</option>)}
                </select>
                <input type="number" placeholder="Days" value={form.retentionDays}
                  onChange={(e) => setForm({ ...form, retentionDays: e.target.value })}
                  className="rounded-lg border border-border bg-background px-3 py-2 text-sm" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setShowForm(false)} className="rounded-lg border border-border px-3 py-1.5 text-sm">Cancel</button>
              <button onClick={create} className="rounded-lg bg-teal-600 hover:bg-teal-700 text-white px-3 py-1.5 text-sm" data-testid="submit-inventory-btn">Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
